// ── Time marks on the model lines ──
// A dot where each model has the storm at the SAME moments, a day apart:
// the official forecast's own daily times when NHC has one (so the dots line
// up with its D/S/H/M points, which carry the time labels), else 00Z each
// day. Dots spread further apart = a model moving the storm faster.

export interface TrackPoint { tau: number; lat: number; lon: number; vmax?: number | null }

const DAY = 86_400_000;
const MAX_DAYS = 7;

export const utcMs = (iso: string) => Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);

/** The moments to mark, after `now`: anchor + whole days (anchor: the
 *  official forecast's start, or else the last 00Z). */
export function markTimes(anchor: number, now: number): number[] {
  const out: number[] = [];
  let t = anchor + Math.max(1, Math.ceil((now - anchor) / DAY)) * DAY;
  for (let k = 0; k < MAX_DAYS; k++, t += DAY) out.push(t);
  return out;
}

export function lastMidnightUtc(now: number): number {
  return Math.floor(now / DAY) * DAY;
}

/** Where a track is at time t — linear between its points (the shorter way
 *  across 180°) — or null outside the track's span. */
export function positionAt(points: TrackPoint[], init: number, t: number): { lat: number; lon: number; vmax: number | null } | null {
  const h = (t - init) / 3_600_000;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i], b = points[i + 1];
    if (h < a.tau || h > b.tau || b.tau === a.tau) continue;
    const f = (h - a.tau) / (b.tau - a.tau);
    const dlon = ((b.lon - a.lon + 540) % 360) - 180;
    const va = a.vmax ?? null, vb = b.vmax ?? null;
    return {
      lat: a.lat + (b.lat - a.lat) * f,
      lon: a.lon + dlon * f,
      vmax: va != null && vb != null && (va > 0 || vb > 0) ? Math.round(va + (vb - va) * f) : null,
    };
  }
  return points.length === 1 && Math.abs(h - points[0].tau) < 1e-6
    ? { lat: points[0].lat, lon: points[0].lon, vmax: points[0].vmax ?? null } : null;
}
